// @flow

import React, { type Node, createContext, useContext, useEffect, useRef } from 'react';

type DocEltContextProviderProps = {
  children: Node,
};

type Position = {
  top: number,
  left: number,
  bottom: number,
  right: number,
};

type DocEltContextValue = {
  getElementCoords: (elem: HTMLElement) => Position,
  getFullHeight: () => number,
};

// Create context for Document Element
const DocEltContext = createContext<DocEltContextValue>({});

// Provider of DocEltContext
export const DocEltContextProvider = ({ children }: DocEltContextProviderProps) => {
  const body = useRef(window.document?.body);
  const docEl = useRef(window.document?.documentElement);

  const scrollTop = useRef(
    window.pageYOffset || docEl.current?.scrollTop || body.current?.scrollTop,
  );
  const scrollLeft = useRef(
    window.pageXOffset || docEl.current?.scrollLeft || body.current?.scrollLeft,
  );

  const clientTop = useRef(docEl.current?.clientTop || body.current?.clientTop || 0);
  const clientLeft = useRef(docEl.current?.clientLeft || body.current?.clientLeft || 0);

  useEffect(() => {
    const handleResize = () => {
      body.current = window.document?.body;
      docEl.current = window.document?.documentElement;

      scrollTop.current = window.pageYOffset || docEl.current?.scrollTop || body.current?.scrollTop;
      scrollLeft.current
        = window.pageXOffset || docEl.current?.scrollLeft || body.current?.scrollLeft;

      clientTop.current = docEl.current?.clientTop || body.current?.clientTop || 0;
      clientLeft.current = docEl.current?.clientLeft || body.current?.clientLeft || 0;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const getElementCoords = (elem: HTMLElement): Position => {
    const box = elem.getBoundingClientRect();

    const top = box.top + scrollTop.current - clientTop.current;
    const left = box.left + scrollLeft.current - clientLeft.current;
    const bottom = top + box.height;
    const right = left + box.width;

    return {
      top: Math.round(top),
      left: Math.round(left),
      bottom: Math.round(bottom),
      right: Math.round(right),
    };
  };

  const getFullHeight = () => docEl.current?.scrollHeight || body.current?.scrollHeight || 0;

  const docEltContext: DocEltContextValue = {
    getElementCoords,
    getFullHeight,
  };

  // Return provider with document element context
  return <DocEltContext.Provider value={docEltContext}>{children}</DocEltContext.Provider>;
};

export const useDocElt = (): DocEltContextValue => useContext(DocEltContext);
